import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { isEmpty, omitBy, isNil } from "lodash";
import { Radio, Row, Col } from "antd";
import { DataTable, Button, BasicModal } from "@life-line-lab/components-react";
import DataTableFilter from "Components/shared/data-table-filter";
import ArchiveSettings from "Components/order-management/shared/parcel-modals/archive-settings";
import useSession from "Hooks/session.hook";
import { UserRoleToAssign } from "Types/user.types";
import useCheckUserRole from "Hooks/check-user-role.hook";

interface IncidentReportListProps {
  allReportList: any;
  allArchivedReportList?: any;
  onFilter?: any;
  columns: any;
  hasArchive?: boolean;
  onDownloadExcelFile?: any;
  tabLabel?: string;
  typeOptions?: any;
  allArchiveObjects?: any;
  isExcelLoading?: any;
}

const useModal = () => {
  const initialModal: any = {
    open: false,
  };

  const [modal, setModal] = useState<any>(initialModal);
  const [callModal, setCallModal] = useState<any>(<></>);

  const handleResetModalState = () => {
    setModal(initialModal);
  };

  return {
    modal,
    setModal,
    callModal,
    setCallModal,
    handleResetModalState,
  };
};

const useSwitchTabs = (props) => {
  const { setArchiveState, handleFetchArchiveSettings } = props || {};
  const { org, roles } = useSession();

  const handleSwitchTabs = (value) => {
    if (value == "archive") {
      setArchiveState(true);
      roles[0]?.type == "COMPANY" && handleFetchArchiveSettings(org._id);
    } else {
      setArchiveState(false);
    }
  };

  return {
    handleSwitchTabs,
  };
};

const useSubmitArchiveOrders = (props) => {
  const { setModal, archiveSettings, handleUpdateArchiveSettings } =
    props || {};

  const handleSetupArchiveSettings = (data) => {
    const archiveSettingInput = {
      archiveSettingID: archiveSettings?._id,
      noOfIncidentReports: data?.noOfItems,
      frequency: data?.frequency,
    };
    handleUpdateArchiveSettings(archiveSettingInput);

    setModal({ open: false });
  };

  return {
    handleSetupArchiveSettings,
  };
};

const FilterComponents = (props) => {
  const {
    archiveState,
    allArchivedReportList,
    allReportList,
    tabLabel,
    hasArchive,
    allObjects,
    isExcelLoading,
  } = props || {};
  const { onDownloadExcelFile, handleSwitchTabs } = props || {};

  const {
    setModal,
    setCallModal,
    handleSetupArchiveSettings,
    handleResetModalState,
    archiveSettings,
  } = allObjects || {};

  const { t } = useTranslation();

  return (
    <Row>
      <Col flex="auto">
        <Row gutter={[8, 0]}>
          <Col>
            {hasArchive && (
              <div className="radio-buttons">
                <Radio.Group
                  defaultValue="latest"
                  buttonStyle="solid"
                  onChange={(value) => {
                    handleSwitchTabs(value.target.value);
                  }}
                >
                  <Radio.Button value="latest">
                    {t("option.latest")}
                  </Radio.Button>
                  <Radio.Button value="archive">
                    {t("option.archive")}
                  </Radio.Button>
                </Radio.Group>
              </div>
            )}
          </Col>
          <Col>
            <div className="mas-container">
              {archiveState && (
                <span className="manage-archive-settings">
                  <a
                    onClick={() => {
                      setCallModal(
                        <ArchiveSettings
                          onSubmit={handleSetupArchiveSettings}
                          onCancel={handleResetModalState}
                          archiveSettings={archiveSettings}
                          type={"INCIDENT_REPORT"}
                        />,
                      );

                      setModal({ open: true });
                    }}
                  >
                    {t("txt.manageArchiveSettings")}
                  </a>
                </span>
              )}
            </div>
          </Col>
        </Row>
      </Col>
      <Col flex="none">
        <Button
          type="secondary"
          isLoading={isExcelLoading}
          label={
            isExcelLoading
              ? t("label.generatingReport")
              : t("action.downloadIncidents")
          }
          onClick={() => {
            onDownloadExcelFile(
              archiveState ? allArchivedReportList : allReportList,
              `${tabLabel} - Incident Reports`,
            );
          }}
        />
      </Col>
    </Row>
  );
};

const IncidentReportList: React.FC<IncidentReportListProps> = ({
  columns,
  allReportList,
  allArchivedReportList,
  onFilter,
  onDownloadExcelFile,
  hasArchive,
  tabLabel,
  typeOptions,
  allArchiveObjects,
  isExcelLoading,
}) => {
  const { t } = useTranslation();
  const { isRole } = useCheckUserRole();

  // filter that is based on the role of user
  const filterOptionsByUserRole = () => {
    if (isRole([UserRoleToAssign.PUBLIC_TRANSPORT_OPERATIONAL_USER])) {
      return [{ label: t("option.train"), value: "TRAIN" }];
    }
    return [
      { label: t("option.all"), value: "" },
      { label: t("option.parcels"), value: "PARCEL" },
      { label: t("option.vehicle"), value: "VEHICLE" },
      { label: t("option.train"), value: "TRAIN" },
      { label: t("option.warehouse"), value: "WAREHOUSE" },
      { label: t("option.customer"), value: "CUSTOMER" },
      { label: t("option.others"), value: "OTHERS" },
    ];
  };

  // set initial filter to be the first if only 1 option is present
  const initialFilter =
    filterOptionsByUserRole().length > 1
      ? {
          type: "",
          category: "",
        }
      : {
          type: "",
          category: filterOptionsByUserRole()[0].value,
        };

  const [filterState, setFilterState] = useState<any>(initialFilter);

  // filter has allowClear=true, which causes filter to be 'All'
  // how to appropriately overcome this issue
  const initialFilterSelection = [
    {
      key: "type",
      name: "type",
      label: "By Type",
      value: filterState?.type,
      options: [{ label: t("option.all"), value: "" }].concat(
        typeOptions?.map((type) => {
          return {
            label: type.name,
            value: type._id,
          };
        }),
      ),
    },
    {
      key: "category",
      name: "category",
      label: "By Category",
      value: filterState?.category,
      options: filterOptionsByUserRole(),
    },
  ];

  const [filterSelection, setFilterSelection] = useState<any>(
    initialFilterSelection,
  );

  // =========================================================
  //   All archive related objects and functions
  // =========================================================
  const {
    archiveSettingsObject,
    archiveStateObject,
    handleFetchArchiveSettings,
    modifySettingsFunctions,
  } = allArchiveObjects || {};
  const { archiveState, setArchiveState } = archiveStateObject || {};
  const { archiveSettings } = archiveSettingsObject || {};
  const { handleUpdateArchiveSettings } = modifySettingsFunctions || {};

  // =========================================================
  //   Modal related settings
  // =========================================================
  const { modal, callModal, handleResetModalState, setModal, setCallModal } =
    useModal();

  // =========================================================
  //   Update archive model
  // =========================================================
  const { handleSetupArchiveSettings } = useSubmitArchiveOrders({
    setModal,
    archiveSettings,
    handleUpdateArchiveSettings,
  });

  // =========================================================
  //   Switch tabs
  // =========================================================
  const { handleSwitchTabs } = useSwitchTabs({
    setArchiveState,
    handleFetchArchiveSettings,
  });

  // =========================================================
  //   Other functions
  // =========================================================
  useEffect(() => {
    if (onFilter && !isEmpty(filterState)) {
      const filterPayload = omitBy(
        filterState,
        (input) => isNil(input) || isEmpty(input),
      );
      onFilter(filterPayload);
    }
  }, [filterState]);

  const handleSearch = async (text: string) => {
    setFilterState((prevFilter) => ({ ...prevFilter, orFields: text }));
  };

  const handleFilterSelection = (selection) => {
    if (onFilter) {
      setFilterState((prevFilter) => ({
        ...prevFilter,
        type: selection.type,
        category: selection.category,
      }));
    }
  };

  useEffect(() => {
    setFilterSelection(initialFilterSelection);
  }, [typeOptions]);

  return (
    <div>
      <DataTable
        columns={columns}
        rowKey={(record: any) => record._id}
        dataSource={archiveState ? allArchivedReportList : allReportList}
        title={() => (
          <Row gutter={[0, 8]}>
            <Col span={24}>
              <div className="filter-component">
                <DataTableFilter
                  searchPlaceholder={t("label.searchBy") + " info"}
                  onSearch={(text: string) => handleSearch(text)}
                  onFilter={(selection) => handleFilterSelection(selection)}
                  renderFilterSelection={filterSelection}
                />
              </div>
            </Col>
            <Col span={24}>
              <FilterComponents
                archiveState={archiveState}
                allArchivedReportList={allArchivedReportList}
                allReportList={allReportList}
                tabLabel={tabLabel}
                hasArchive={hasArchive}
                onDownloadExcelFile={onDownloadExcelFile}
                handleSwitchTabs={handleSwitchTabs}
                allObjects={{
                  setModal,
                  setCallModal,
                  handleSetupArchiveSettings,
                  handleResetModalState,
                  archiveSettings,
                }}
                isExcelLoading={isExcelLoading}
              />
            </Col>
          </Row>
        )}
      />
      <BasicModal
        className="modal"
        isOpen={modal.open}
        isClosable={true}
        isMaskClosable={false}
        onClose={() => {
          handleResetModalState();
        }}
      >
        {/*call the state in the basic modal*/}
        {callModal}
      </BasicModal>
    </div>
  );
};
export default IncidentReportList;
